/**
 * Enriquecimento progressivo das calculadoras do post (E1.3).
 *
 * O bloco `<div data-ds-block="calculator" data-kind="...">` chega do SSR já
 * com uma tabela de exemplo — HTML de verdade, indexável, que continua fazendo
 * sentido sozinho sem JS nenhum (ver cabeçalho de
 * `src/shared/blog/blocks/registry.ts`). Esta função entra DEPOIS, no
 * cliente, e ACRESCENTA os campos interativos por cima desse mesmo HTML —
 * nunca substitui a tabela por uma versão só-React, porque o crawler e o
 * conversor de Markdown (`toMarkdown.ts`) nunca executam este arquivo.
 *
 * A matemática mora em `src/shared/blog/blocks/calculators.ts` (funções
 * puras, sem DOM) — este módulo só monta o formulário, lê os campos e liga o
 * cálculo. `<input type="number">` é usado aqui de propósito e não é uma
 * violação da regra de "use `<NumberField>` do Design System": este arquivo
 * roda fora da árvore React, sobre HTML estático já servido — o `NumberField`
 * é um componente React e não existe fora de um render do React. É uma
 * exceção de arquitetura, não uma folga na regra.
 */

import {
  calcularJurosParcelamento,
  calcularPdd,
  calcularRoiReguaCobranca,
  type CalcResult,
} from "@/shared/blog/blocks/calculators";
import { CALCULATOR_KINDS } from "@/shared/blog/blocks/registry";
import { trackBlogEvent } from "./blogAnalytics";

type CalculatorKind = (typeof CALCULATOR_KINDS)[number];

export interface HydrateCalculatorsOptions {
  /** Slug do post, para o evento `calc_used`. Sem slug, o evento é omitido — o
   *  cálculo continua funcionando normalmente (telemetria nunca é requisito
   *  para a funcionalidade). */
  slug?: string;
  postId?: string;
}

const HYDRATED_ATTR = "data-calc-hydrated";

const moedaFmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const percentFmt = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatMoeda = (valor: number): string => moedaFmt.format(valor);
const formatPercent = (fracaoOuNumero: number, jaEhPercentual = false): string =>
  `${percentFmt.format(jaEhPercentual ? fracaoOuNumero : fracaoOuNumero * 100)}%`;

let uid = 0;
/** Prefixo único por bloco hidratado — evita colisão de `id`/`for` quando há
 *  mais de uma calculadora na mesma página. */
const nextUid = (): string => `calc-${uid++}`;

// ---------------------------------------------------------------------------
// Helpers de DOM — só `textContent`, nunca `innerHTML` com valor calculado.
// ---------------------------------------------------------------------------

interface NumberFieldOptions {
  id: string;
  label: string;
  step?: string;
  min?: string;
  max?: string;
  defaultValue?: number;
}

/** Campo numérico com `<label>` associado por `for`/`id` — a base de
 *  acessibilidade e navegação por teclado exigida pela tarefa. */
const createNumberField = (opts: NumberFieldOptions): { wrap: HTMLDivElement; input: HTMLInputElement } => {
  const wrap = document.createElement("div");
  wrap.className = "flex flex-col gap-1";

  const label = document.createElement("label");
  label.setAttribute("for", opts.id);
  label.className = "text-ds-small font-medium text-ds-ink-700";
  label.textContent = opts.label;

  const input = document.createElement("input");
  input.type = "number";
  input.id = opts.id;
  input.name = opts.id;
  input.inputMode = "decimal";
  if (opts.step) input.step = opts.step;
  if (opts.min) input.min = opts.min;
  if (opts.max) input.max = opts.max;
  if (opts.defaultValue !== undefined) input.value = String(opts.defaultValue);
  input.className =
    "rounded-ds-lg border border-ds-border-strong bg-ds-surface px-3 py-2 text-ds-body text-ds-ink-900 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus";

  wrap.append(label, input);
  return { wrap, input };
};

const readNumber = (input: HTMLInputElement): number => input.valueAsNumber;

const createResultRegion = (): HTMLDivElement => {
  const region = document.createElement("div");
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  region.className = "mt-3 rounded-ds-lg border border-ds-border bg-ds-surface-sunken p-3 text-ds-body text-ds-ink-900";
  region.textContent = "Preencha os campos e calcule.";
  return region;
};

const renderErro = (region: HTMLElement, mensagem: string): void => {
  region.textContent = "";
  const p = document.createElement("p");
  p.className = "text-ds-danger-700";
  p.textContent = mensagem;
  region.append(p);
};

/** Uma linha "rótulo: valor" dentro da região de resultado. */
const appendResultLine = (region: HTMLElement, rotulo: string, valor: string): void => {
  const p = document.createElement("p");
  p.className = "flex justify-between gap-4";
  const spanRotulo = document.createElement("span");
  spanRotulo.className = "text-ds-ink-700";
  spanRotulo.textContent = rotulo;
  const spanValor = document.createElement("span");
  spanValor.className = "font-semibold tabular-nums text-ds-ink-900";
  spanValor.textContent = valor;
  p.append(spanRotulo, spanValor);
  region.append(p);
};

const createButton = (texto: string): HTMLButtonElement => {
  const button = document.createElement("button");
  button.type = "submit";
  button.textContent = texto;
  button.className =
    "cursor-pointer self-start rounded-ds-lg bg-ds-brand-600 px-4 py-2 text-ds-small font-semibold text-ds-on-solid " +
    "hover:bg-ds-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus " +
    "disabled:cursor-not-allowed disabled:opacity-60";
  return button;
};

const createForm = (): HTMLFormElement => {
  const form = document.createElement("form");
  form.className = "mt-4 flex flex-col gap-3 border-t border-ds-border pt-4";
  form.noValidate = true;
  return form;
};

const emitCalcUsed = (options: HydrateCalculatorsOptions, kind: CalculatorKind): void => {
  if (!options.slug) return;
  trackBlogEvent(options.slug, "calc_used", { kind }, options.postId);
};

// ---------------------------------------------------------------------------
// Um "builder" por tipo: monta o formulário, liga o submit e devolve a
// função de limpeza que desfaz exatamente o que foi acrescentado.
// ---------------------------------------------------------------------------

type Builder = (block: HTMLElement, options: HydrateCalculatorsOptions) => () => void;

const buildJurosParcelamento: Builder = (block, options) => {
  const id = nextUid();
  const form = createForm();

  const { wrap: wrapAVista, input: inputAVista } = createNumberField({
    id: `${id}-avista`,
    label: "Valor à vista (R$)",
    step: "0.01",
    min: "0.01",
  });
  const { wrap: wrapParcelas, input: inputParcelas } = createNumberField({
    id: `${id}-parcelas`,
    label: "Número de parcelas",
    step: "1",
    min: "1",
  });
  const { wrap: wrapParcela, input: inputParcela } = createNumberField({
    id: `${id}-parcela`,
    label: "Valor de cada parcela (R$)",
    step: "0.01",
    min: "0.01",
  });

  const button = createButton("Calcular taxa de juros");
  const region = createResultRegion();

  form.append(wrapAVista, wrapParcelas, wrapParcela, button);

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    const resultado = calcularJurosParcelamento({
      valorAVista: readNumber(inputAVista),
      numeroParcelas: readNumber(inputParcelas),
      valorParcela: readNumber(inputParcela),
    });

    if (!resultado.ok) {
      renderErro(region, resultado.mensagem);
      return;
    }

    region.textContent = "";
    appendResultLine(region, "Taxa de juros mensal", `${percentFmt.format(resultado.data.taxaMensalPercentual)}% a.m.`);
    appendResultLine(region, "CET aproximado (12 meses)", formatPercent(resultado.data.cetAproximadoAnual));
    appendResultLine(region, "Total pago", formatMoeda(resultado.data.totalPago));
    appendResultLine(region, "Juros pago no total", formatMoeda(resultado.data.jurosTotal));
    emitCalcUsed(options, "juros-parcelamento");
  };

  form.addEventListener("submit", onSubmit);
  block.append(form, region);

  return () => {
    form.removeEventListener("submit", onSubmit);
    form.remove();
    region.remove();
  };
};

const FAIXAS_PDD = ["0-30 dias", "31-60 dias", "61-90 dias", "91-180 dias", "Acima de 180 dias"] as const;

const buildPdd: Builder = (block, options) => {
  const id = nextUid();
  const form = createForm();
  const grid = document.createElement("div");
  grid.className = "grid grid-cols-1 gap-3 sm:grid-cols-2";

  // Percentual é PARAMETRIZÁVEL (ver comentário em `calculators.ts`): o
  // default começa em 0 de propósito — nada aqui sugere que exista um
  // percentual "correto" por faixa, cada rede define a própria política.
  const linhas = FAIXAS_PDD.map((rotulo, index) => {
    const { wrap: wrapSaldo, input: inputSaldo } = createNumberField({
      id: `${id}-saldo-${index}`,
      label: `Saldo em atraso — ${rotulo} (R$)`,
      step: "0.01",
      min: "0",
      defaultValue: 0,
    });
    const { wrap: wrapPercentual, input: inputPercentual } = createNumberField({
      id: `${id}-percentual-${index}`,
      label: `Provisão — ${rotulo} (%)`,
      step: "0.01",
      min: "0",
      max: "100",
      defaultValue: 0,
    });
    grid.append(wrapSaldo, wrapPercentual);
    return { rotulo, inputSaldo, inputPercentual };
  });

  const button = createButton("Calcular provisão (PDD)");
  const region = createResultRegion();

  form.append(grid, button);

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    const resultado = calcularPdd(
      linhas.map(linha => ({
        rotulo: linha.rotulo,
        saldo: readNumber(linha.inputSaldo),
        percentual: readNumber(linha.inputPercentual) / 100,
      }))
    );

    if (!resultado.ok) {
      renderErro(region, resultado.mensagem);
      return;
    }

    region.textContent = "";
    for (const faixa of resultado.data.faixas) {
      appendResultLine(region, `Provisão — ${faixa.rotulo}`, formatMoeda(faixa.provisao));
    }
    appendResultLine(region, "Saldo total em atraso", formatMoeda(resultado.data.totalSaldo));
    appendResultLine(region, "Provisão total", formatMoeda(resultado.data.totalProvisao));
    emitCalcUsed(options, "pdd");
  };

  form.addEventListener("submit", onSubmit);
  block.append(form, region);

  return () => {
    form.removeEventListener("submit", onSubmit);
    form.remove();
    region.remove();
  };
};

const buildRoiReguaCobranca: Builder = (block, options) => {
  const id = nextUid();
  const form = createForm();

  const { wrap: wrapCarteira, input: inputCarteira } = createNumberField({
    id: `${id}-carteira`,
    label: "Carteira em atraso (R$)",
    step: "0.01",
    min: "0",
  });
  const { wrap: wrapAtual, input: inputAtual } = createNumberField({
    id: `${id}-atual`,
    label: "Taxa de recuperação atual (%)",
    step: "0.01",
    min: "0",
    max: "100",
  });
  const { wrap: wrapEsperada, input: inputEsperada } = createNumberField({
    id: `${id}-esperada`,
    label: "Taxa de recuperação esperada (%)",
    step: "0.01",
    min: "0",
    max: "100",
  });
  const { wrap: wrapCusto, input: inputCusto } = createNumberField({
    id: `${id}-custo`,
    label: "Custo mensal da ferramenta (R$)",
    step: "0.01",
    min: "0.01",
  });

  const button = createButton("Calcular ROI da régua");
  const region = createResultRegion();

  form.append(wrapCarteira, wrapAtual, wrapEsperada, wrapCusto, button);

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    const resultado = calcularRoiReguaCobranca({
      carteiraEmAtraso: readNumber(inputCarteira),
      taxaRecuperacaoAtual: readNumber(inputAtual) / 100,
      taxaRecuperacaoEsperada: readNumber(inputEsperada) / 100,
      custoMensalFerramenta: readNumber(inputCusto),
    });

    if (!resultado.ok) {
      renderErro(region, resultado.mensagem);
      return;
    }

    region.textContent = "";
    appendResultLine(region, "Ganho bruto mensal", formatMoeda(resultado.data.ganhoBrutoMensal));
    appendResultLine(region, "Ganho incremental mensal (líquido)", formatMoeda(resultado.data.ganhoIncrementalMensal));
    appendResultLine(
      region,
      "Payback",
      resultado.data.paybackMeses === null ? "Não se paga com esses números" : `${percentFmt.format(resultado.data.paybackMeses)} meses`
    );
    appendResultLine(
      region,
      "ROI em 12 meses",
      // `roi12Meses` é FRAÇÃO (4 = 400%), como o CET logo acima — não passe
      // `jaEhPercentual`, senão o exemplo da própria tabela do bloco mostra
      // "4%" onde deveria mostrar "400%".
      resultado.data.roi12Meses === null ? "—" : formatPercent(resultado.data.roi12Meses)
    );
    emitCalcUsed(options, "roi-regua-cobranca");
  };

  form.addEventListener("submit", onSubmit);
  block.append(form, region);

  return () => {
    form.removeEventListener("submit", onSubmit);
    form.remove();
    region.remove();
  };
};

const BUILDERS: Record<CalculatorKind, Builder> = {
  "juros-parcelamento": buildJurosParcelamento,
  pdd: buildPdd,
  "roi-regua-cobranca": buildRoiReguaCobranca,
};

const isKnownKind = (kind: string | null): kind is CalculatorKind =>
  !!kind && (CALCULATOR_KINDS as readonly string[]).includes(kind);

/**
 * Encontra todo `[data-ds-block="calculator"]` dentro de `container`, monta
 * os campos e liga o cálculo por cima do HTML já servido pelo SSR.
 *
 * - Idempotente: um bloco já hidratado (`data-calc-hydrated="true"`) é
 *   ignorado — chamar duas vezes não duplica campo.
 * - `data-kind` desconhecido (fora de `CALCULATOR_KINDS`) não faz nada e não
 *   lança — o bloco continua mostrando só a tabela de exemplo do SSR.
 * - Devolve uma função de limpeza que remove tudo o que foi acrescentado
 *   (formulário + região de resultado) e desmarca o bloco, deixando-o pronto
 *   para ser hidratado de novo.
 */
export const hydrateCalculators = (
  container: HTMLElement | null | undefined,
  options: HydrateCalculatorsOptions = {}
): (() => void) => {
  if (!container) return () => {};

  const blocks = container.querySelectorAll<HTMLElement>('[data-ds-block="calculator"]');
  const cleanups: Array<() => void> = [];

  blocks.forEach(block => {
    if (block.getAttribute(HYDRATED_ATTR) === "true") return;

    const kind = block.getAttribute("data-kind");
    if (!isKnownKind(kind)) return;

    block.setAttribute(HYDRATED_ATTR, "true");

    try {
      const cleanupBuilder = BUILDERS[kind](block, options);
      cleanups.push(() => {
        cleanupBuilder();
        block.removeAttribute(HYDRATED_ATTR);
      });
    } catch {
      // Uma calculadora quebrando não pode derrubar o post inteiro — desfaz a
      // marca de hidratado para não deixar o bloco travado num estado morto.
      block.removeAttribute(HYDRATED_ATTR);
    }
  });

  return () => {
    for (const cleanup of cleanups) {
      try {
        cleanup();
      } catch {
        // no-op — limpeza nunca deve lançar.
      }
    }
  };
};

// Reexportado só para o teste conferir o tipo sem duplicar o literal.
export type { CalcResult };
